import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { Modal, Portal, Button, Text, useTheme, Card, IconButton, TextInput, Chip } from 'react-native-paper';
import { apiFetch, createBillingPortalSession } from '../api/apiService';
import { API_BASE, API_ENDPOINTS } from '../api/config';
import { useTranslation } from 'react-i18next';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import { LoadingScreen } from '@/src/components/LoadingScreen';
import { billingCycleForPlan, featureForPlan } from '../features/onboarding/ownerPlanSelection';

interface Plan {
  id: string;
  price_id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product: {
    product_id: string;
    name: string;
    description: string;
    metadata: any;
  };
  metadata: any;
  /** GPS/combo: one-time setup fee price (optional) */
  setup_fee_price_id?: string;
  setup_fee_unit_amount?: number;
  setup_fee_currency?: string;
}

interface PlanSelectionModalProps {
  visible: boolean;
  onDismiss: () => void;
  onPlanSelected: (plan: Plan, quantity: number) => void;
  isTrial?: boolean;
  featureType?: 'hours' | 'gps' | 'combo';
  /** Pre-fill the license quantity (#208 chat-flow hands the owner's choice over). */
  initialQuantity?: number;
  /** Error from the parent's async purchase step (license-purchase-400 fix):
   *  surfaced inline so a failed create_subscription keeps this modal open for a
   *  retry instead of tearing down the whole flow. */
  errorMessage?: string | null;
  /** Neutral information shown above the plans (e.g. the GPS contract was declined, ALL-134). */
  notice?: React.ReactNode;
}

export function PlanSelectionModal({ visible, onDismiss, onPlanSelected, isTrial = true, featureType, initialQuantity, errorMessage, notice }: PlanSelectionModalProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [fetchingPlans, setFetchingPlans] = useState(false);
  const [quantityValue, setQuantityValue] = useState(
    initialQuantity && initialQuantity >= 1 ? String(initialQuantity) : '1'
  );

  // Keep the pre-filled quantity in sync when the modal is (re)opened with a
  // fresh choice from the licenses screen.
  useEffect(() => {
    if (visible && initialQuantity && initialQuantity >= 1) {
      setQuantityValue(String(initialQuantity));
    }
  }, [visible, initialQuantity]);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation('onboarding');
  const theme = useTheme();
  const [currentFeature, setCurrentFeature] = useState<'hours' | 'gps' | 'combo'>(featureType || 'hours');
  const [existingByPriceId, setExistingByPriceId] = useState<Record<string, boolean>>({});
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  
  const isAnnualPlan = (plan: Plan) => billingCycleForPlan(plan) === 'annual';

  // Only Hours monthly uses the no-card trial contract.
  const isTrialAllowed = (plan?: Plan | null) =>
    isTrial && !!plan && featureForPlan(plan) === 'hours' && billingCycleForPlan(plan) === 'monthly';

  useEffect(() => {
    if (visible) {
      fetchAvailablePlans();
    }
  }, [visible, currentFeature]);

  // Close modal when returning from Stripe portal
  useEffect(() => {
    if (!visible) return;
    
    const checkStripeReturn = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('stripe_portal_return') === 'true') {
          onDismiss();
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    };
    
    checkStripeReturn();
    
    // Listen for focus (when tab becomes active again)
    if (Platform.OS === 'web') {
      window.addEventListener('focus', checkStripeReturn);
      return () => window.removeEventListener('focus', checkStripeReturn);
    }
  }, [visible, onDismiss]);

  const fetchAvailablePlans = async () => {
    setFetchingPlans(true);
    setError(null);
    try {
      // Build URL with feature filter from internal toggle (falls back to prop if provided)
      const feature = featureType || currentFeature;
      let url = `${API_ENDPOINTS.SUBSCRIPTIONS}prices/`;
      if (feature) {
        url += `?feature=${feature}`;
      }
      
      
      const response = await apiFetch<{ prices: Plan[] }>(url);
      
      if (!response.ok) {
        throw new Error('plan_fetch_failed');
      }
      
      const mapped = response.data.prices
        .filter(p => billingCycleForPlan(p) !== null)
        .map(p => ({
          ...p,
          id: p.price_id,
        } as Plan));
      
      
      setPlans(mapped);
      if (mapped.length > 0) {
        // Select monthly plan by default
        const monthlyPlan = mapped.find(p => billingCycleForPlan(p) === 'monthly');
        setSelectedPlan(monthlyPlan || mapped[0]);
      }
      // After setting plans, check which ones already exist and if customer exists
      try {
        setCheckingExisting(true);
        
        // Check if user has Stripe customer
        try {
          const customerResp = await apiFetch<any>(API_ENDPOINTS.USER_DETAILS);
          const hasCustomer = !!customerResp.data?.stripe_customers?.length;
          setHasStripeCustomer(hasCustomer);
        } catch {
          setHasStripeCustomer(false);
        }
        
        const entries = await Promise.all(mapped.map(async (p) => {
          try {
            const resp = await apiFetch<{ subscription: any }>(
              `${API_ENDPOINTS.SUBSCRIPTIONS}check_subscription/`,
              { method: 'POST', body: JSON.stringify({ price_id: p.price_id }) }
            );
            return [p.price_id, !!resp.data?.subscription] as const;
          } catch {
            return [p.price_id, false] as const;
          }
        }));
        const map: Record<string, boolean> = {};
        entries.forEach(([priceId, exists]) => { map[priceId] = exists; });
        setExistingByPriceId(map);
      } finally {
        setCheckingExisting(false);
      }
    } catch (e: any) {
      console.error('Error fetching plans:', e);
      setError(t('errorFetchingPlans'));
    } finally {
      setFetchingPlans(false);
    }
  };

  const hasSetupFee = (plan: Plan) =>
    plan.setup_fee_unit_amount != null && plan.setup_fee_unit_amount > 0;

  const currencyLocale = i18n.language === 'pt_BR'
    ? 'pt-BR'
    : i18n.language === 'pt'
      ? 'pt-PT'
      : 'en-US';

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat(currencyLocale, { style: 'currency', currency: currency.toUpperCase() }).format(amount);

  const getPlanFeature = (plan: Plan): 'hours' | 'gps' | 'combo' | null => {
    const feature = plan.product?.metadata?.feature || plan.metadata?.feature;
    return feature === 'hours' || feature === 'gps' || feature === 'combo' ? feature : null;
  };

  const getPlanName = (plan: Plan) => {
    const feature = getPlanFeature(plan);
    return feature ? t(`productOption_${feature}`) : plan.product.name;
  };

  const getPlanDescription = (plan: Plan) => {
    const feature = getPlanFeature(plan);
    return feature ? t(`productOption_${feature}Description`) : plan.product.description;
  };

  const getFormattedPrice = (plan: Plan) => {
    const formatter = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: plan.currency.toUpperCase(),
    });
    const billingCycle = billingCycleForPlan(plan);
    const intervalKey = billingCycle === 'annual'
      ? 'year'
      : billingCycle === 'monthly'
        ? 'month'
        : plan.interval;
    return `${formatter.format(plan.unit_amount)}/${t(`interval.${intervalKey}`)}`;
  };

  const getTotalPrice = () => {
    if (!selectedPlan) return '';
    
    const formatter = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: selectedPlan.currency.toUpperCase(),
    });
    
    const total = selectedPlan.unit_amount * parseInt(quantityValue, 10);
    return formatter.format(total);
  };

  const handleQuantityChange = (text: string) => {
    const positiveInteger = text.replace(/[^0-9]/g, '');
    setQuantityValue(positiveInteger === '' ? '1' : positiveInteger);
  };

  const incrementQuantity = () => {
    const currentQty = parseInt(quantityValue, 10);
    setQuantityValue((currentQty + 1).toString());
  };

  const decrementQuantity = () => {
    const currentQty = parseInt(quantityValue, 10);
    if (currentQty > 1) {
      setQuantityValue((currentQty - 1).toString());
    }
  };

  const handleContinue = async () => {
    if (!selectedPlan) return;
    
    // If subscription exists and customer exists, open Stripe portal
    if (existingByPriceId[selectedPlan.price_id] && hasStripeCustomer) {
      try {
        // For web: use current origin
        // For mobile: use deep link directly so Stripe redirects to app
        let returnUrl: string;
        let redirectUrl: string | undefined;
        
        if (Platform.OS === 'web') {
          returnUrl = window.location.origin + '/dashboard/home?stripe_portal_return=true';
        } else {
          // Mobile: Use the app's deep link directly as the returnUrl to pass to backend metadata.
          // This is the URL we ultimately want to redirect to from the backend.
          returnUrl = Linking.createURL('/dashboard/home');
          redirectUrl = Linking.createURL('/dashboard/home'); // What openAuthSessionAsync listens for
        }
        
        // The URL sent to Stripe is always an HTTPS URL, using the baseOrigin and backend redirect endpoint.
        // The actual frontend return URL (which can be HTTP or deep link) is sent in metadata.
        let stripeApiReturnUrl: string;
        if (Platform.OS === 'web') {
          stripeApiReturnUrl = window.location.origin + '/api/subscriptions/billing-portal-redirect/?mobile=false';
        } else {
          stripeApiReturnUrl = `${API_BASE}/api/subscriptions/billing-portal-redirect/?mobile=true`;
        }

        const response = await createBillingPortalSession(
          returnUrl,
          stripeApiReturnUrl,
          Platform.OS !== 'web' // Pass is_mobile flag
        );
        if (response.ok && response.data?.url) {
          if (Platform.OS === 'web') {
            window.open(response.data.url, '_blank');
            // Don't dismiss - wait for return with URL param
          } else {
            const result = await WebBrowser.openAuthSessionAsync(response.data.url, redirectUrl!);
            if (result.type === 'success' || result.type === 'dismiss') {
              onDismiss();
            }
          }
        } else {
          console.error('Invalid billing portal response:', response);
          setError(t('errorOpeningPortal'));
        }
      } catch (error) {
        console.error('Error opening billing portal:', error);
        setError(t('errorOpeningPortal'));
      }
    } else {
      // New subscription
      onPlanSelected(selectedPlan, parseInt(quantityValue, 10));
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        testID="plan-selection-modal"
        contentContainerStyle={[
          styles.container, 
          { backgroundColor: theme.colors.background }
        ]}
        dismissable={true}
      >
        <View style={[styles.header, { borderBottomColor: theme.colors.outline }]}>
          <Text variant="headlineMedium" style={{ color: theme.colors.primary }}>
            {t('ownerDashboard.choosePlan')}
          </Text>
          <IconButton
            icon="close"
            size={24}
            onPress={onDismiss}
            iconColor={theme.colors.primary}
          />
        </View>

        {notice ? (
          <View testID="plan-selection-notice" style={styles.notice}>
            {notice}
          </View>
        ) : null}

        {/* Feature type toggle */}
        <View style={{ paddingHorizontal: 16, paddingTop: 12 }}>
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
            <Button
              mode={currentFeature === 'hours' ? 'contained' : 'outlined'}
              onPress={() => { if (currentFeature !== 'hours') { setCurrentFeature('hours'); setSelectedPlan(null); } }}
            >
              {t('planType.hours')}
            </Button>
            <Button
              mode={currentFeature === 'gps' ? 'contained' : 'outlined'}
              onPress={() => { if (currentFeature !== 'gps') { setCurrentFeature('gps'); setSelectedPlan(null); } }}
            >
              {t('planType.gps')}
            </Button>
            <Button
              mode={currentFeature === 'combo' ? 'contained' : 'outlined'}
              onPress={() => { if (currentFeature !== 'combo') { setCurrentFeature('combo'); setSelectedPlan(null); } }}
            >
              {t('planType.combo')}
            </Button>
          </View>
        </View>
        
        <ScrollView 
          style={[styles.scrollContainer, { backgroundColor: theme.colors.background }]} 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          bounces={true}
        >
          {fetchingPlans || checkingExisting ? (
            <LoadingScreen />
          ) : plans.length === 0 ? (
            <View style={styles.loadingContainer}>
              <Text style={{ color: theme.colors.error }}>{error || t('noPlansAvailable')}</Text>
            </View>
          ) : (
            <>
              <View style={styles.plansContainer}>
                {plans.map((plan) => (
                  <Card
                    key={plan.id}
                    testID={`plan-card-${plan.price_id}`}
                    style={[
                      styles.planCard,
                      { backgroundColor: theme.colors.surface },
                      selectedPlan?.id === plan.id && {
                        borderColor: theme.colors.primary,
                        borderWidth: 2,
                      }
                    ]}
                    onPress={() => setSelectedPlan(plan)}
                  >
                    <Card.Content>
                      <View style={styles.planHeader}>
                        <View style={styles.planInfo}>
                          <Text variant="titleLarge" style={{ color: theme.colors.primary }}>
                            {getPlanName(plan)}
                          </Text>
                          {hasSetupFee(plan) ? (
                            <>
                              <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                                {t('buyLicense.setupFee')}: {formatCurrency(plan.setup_fee_unit_amount!, plan.setup_fee_currency || plan.currency)} ({t('buyLicense.once')})
                              </Text>
                              <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                                {t('buyLicense.monthlyAmount')}: {getFormattedPrice(plan)}
                              </Text>
                            </>
                          ) : (
                            <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                              {getFormattedPrice(plan)}
                            </Text>
                          )}
                          {(plan?.product?.metadata?.feature === 'combo' || plan?.metadata?.feature === 'combo') && (
                            <Chip mode="flat" style={{ alignSelf: 'flex-start', marginTop: 8 }} icon="link-variant">
                              {t('planType.combo')}
                            </Chip>
                          )}
                          {isAnnualPlan(plan) && (
                            <Text variant="bodyMedium" style={{ color: theme.colors.primary, marginTop: 4 }}>
                              {t('ownerDashboard.save10Percent')}
                            </Text>
                          )}
                          {isTrialAllowed(plan) && (
                            <Text variant="bodyMedium" style={{ color: theme.colors.primary, marginTop: 4 }}>
                              {t('ownerDashboard.14daystrial')}
                            </Text>
                          )}
                        {existingByPriceId[plan.price_id] && hasStripeCustomer && (
                          <Chip mode="outlined" style={{ alignSelf: 'flex-start', marginTop: 8 }} icon="open-in-new">
                            {t('manageInStripe')}
                          </Chip>
                        )}
                        </View>
                      </View>
                      
                      <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, marginTop: 16 }}>
                        {getPlanDescription(plan)}
                      </Text>

                      {plan.metadata?.features && Array.isArray(plan.metadata.features) && (
                        <View style={styles.featuresContainer}>
                          {plan.metadata.features.map((feature: string, idx: number) => (
                            <Text key={idx} style={[styles.featureItem, { color: theme.colors.onSurfaceVariant }]}>
                              • {feature}
                            </Text>
                          ))}
                        </View>
                      )}
                    </Card.Content>
                  </Card>
                ))}
              </View>

              {selectedPlan && !(existingByPriceId[selectedPlan.price_id] && hasStripeCustomer) && (
              <View style={styles.quantityContainer}>
                <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
                  {t('buyLicense.selectQuantity')}
                </Text>
                <View style={styles.quantityInputContainer}>
                  <IconButton
                    icon="minus"
                    size={24}
                    onPress={decrementQuantity}
                    disabled={parseInt(quantityValue, 10) <= 1}
                    style={styles.quantityButton}
                    iconColor={theme.colors.primary}
                  />
                  <TextInput
                    label={t('buyLicense.quantity')}
                    value={quantityValue}
                    onChangeText={handleQuantityChange}
                    keyboardType="numeric"
                    style={styles.quantityInput}
                    mode="outlined"
                    dense
                    testID="owner-combo-quantity-input"
                  />
                  <IconButton
                    icon="plus"
                    size={24}
                    onPress={incrementQuantity}
                    style={styles.quantityButton}
                    iconColor={theme.colors.primary}
                  />
                </View>
              </View>
              )}

              {selectedPlan && !(existingByPriceId[selectedPlan.price_id] && hasStripeCustomer) && (
              <View style={[styles.summaryContainer, { borderTopColor: theme.colors.outline }]}> 
                <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
                  {t('buyLicense.summary')}
                </Text>
                
                <View style={styles.summaryRow}>
                  <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('buyLicense.plan')}:</Text>
                  <Text style={{ color: theme.colors.onSurface }}>{selectedPlan ? getPlanName(selectedPlan) : ''}</Text>
                </View>
                
                <View style={styles.summaryRow}>
                  <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('buyLicense.licenses')}:</Text>
                  <Text style={{ color: theme.colors.onSurface }}>{quantityValue}</Text>
                </View>

                {hasSetupFee(selectedPlan) && (
                  <>
                    <View style={styles.summaryRow}>
                      <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('buyLicense.setupFee')} ({t('buyLicense.once')}):</Text>
                      <Text style={{ color: theme.colors.onSurface }}>{formatCurrency(selectedPlan.setup_fee_unit_amount! * parseInt(quantityValue, 10), selectedPlan.setup_fee_currency || selectedPlan.currency)}</Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('buyLicense.monthlyAmount')}:</Text>
                      <Text style={{ color: theme.colors.onSurface }}>{getTotalPrice()}</Text>
                    </View>
                  </>
                )}
                {!hasSetupFee(selectedPlan) && (
                  <View style={styles.summaryRow}>
                    <Text style={[styles.totalText, { color: theme.colors.primary }]}>{t('buyLicense.total')}:</Text>
                    <Text style={[styles.totalPrice, { color: theme.colors.primary }]}>{getTotalPrice()}</Text>
                  </View>
                )}
                {hasSetupFee(selectedPlan) && (
                  <View style={styles.summaryRow}>
                    <Text style={[styles.totalText, { color: theme.colors.primary }]}>{t('buyLicense.totalFirstInvoice')}:</Text>
                    <Text style={[styles.totalPrice, { color: theme.colors.primary }]}>
                      {formatCurrency(
                        (selectedPlan.setup_fee_unit_amount! + selectedPlan.unit_amount) * parseInt(quantityValue, 10),
                        selectedPlan.currency
                      )}
                    </Text>
                  </View>
                )}
              </View>
              )}

              {error && (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
              )}
            </>
          )}
        </ScrollView>

        {errorMessage ? (
          <Text
            testID="purchase-error"
            style={[styles.errorText, { color: theme.colors.error, paddingHorizontal: 16 }]}
          >
            {errorMessage}
          </Text>
        ) : null}

        <View style={[styles.footer, { borderTopColor: theme.colors.outline, backgroundColor: theme.colors.surface }]}>
          <Button
            mode="contained"
            testID="plan-confirm"
            onPress={handleContinue}
            disabled={!selectedPlan}
            style={styles.footerButton}
            contentStyle={{ paddingVertical: 4 }}
            icon={selectedPlan && existingByPriceId[selectedPlan.price_id] && hasStripeCustomer ? "open-in-new" : "credit-card"}
          >
            {selectedPlan && existingByPriceId[selectedPlan.price_id] && hasStripeCustomer
              ? t('manageInStripe')
              : (isTrialAllowed(selectedPlan)
                  ? t('ownerDashboard.startTrial')
                  : t('ownerDashboard.buyNow'))}
          </Button>
        </View>
      </Modal>
    </Portal>
  );
}

const styles = StyleSheet.create({
  container: { 
    padding: 0, 
    borderRadius: 8,
    ...(Platform.OS === 'web' ? {
      maxWidth: 1000,
      width: '95%',
      maxHeight: '90%',
      alignSelf: 'center',
      margin: 20,
    } : {
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      margin: 0,
      borderRadius: 0,
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  scrollContainer: {
    flex: Platform.OS === 'web' ? 0 : 1,
    maxHeight: Platform.OS === 'web' ? 600 : undefined,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    width: '100%',
    flex: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 24,
    marginBottom: 12,
  },
  plansContainer: {
    gap: 8,
    width: '100%',
    paddingBottom: 8,
  },
  planCard: {
    marginVertical: 6,
  },
  planHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  planInfo: {
    flex: 1,
  },
  featuresContainer: {
    marginTop: 16,
  },
  featureItem: {
    fontSize: 16,
    marginBottom: 8,
    lineHeight: 22,
  },
  quantityContainer: {
    marginTop: 16,
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    justifyContent: 'center',
  },
  quantityInput: {
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 12,
  },
  quantityButton: {
    margin: 0,
  },
  summaryContainer: {
    marginTop: 16,
    borderTopWidth: 1,
    paddingTop: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  totalText: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  totalPrice: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  errorText: {
    textAlign: 'center',
    marginTop: 16,
  },
  notice: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    borderTopWidth: 1,
    width: '100%',
  },
  footerButton: {
    flex: 1,
    marginHorizontal: 4,
  },
}); 